/**
 * Achievements — pure data + a tiny evaluator. No React, no engine
 * coupling. The HUD watches game state and calls `evaluateAchievements`
 * after each meaningful change; newly-unlocked ids are persisted via the
 * gameStore and surfaced as toasts.
 *
 * Adding an achievement: append to ACHIEVEMENTS with a `predicate` that
 * reads from the evaluation context. Predicates must be pure + cheap —
 * they may fire many times per second.
 */

export interface Achievement {
  id: string;
  name: string;
  description: string;
  /** Lucide-style single-char glyph, used when no SVG is wired up. */
  glyph: string;
  /** Accent color for the badge — hue 0–360. */
  hue: number;
  predicate: (ctx: AchievementContext) => boolean;
}

export interface AchievementContext {
  /** Player's kills THIS life. */
  kills: number;
  /** Player's current mass. */
  mass: number;
  /** All-time best mass across all runs. */
  bestMass: number;
  /** Current combo size (1+). */
  combo: number;
  /** Total matches completed (length of history). */
  matchesPlayed: number;
}

export const ACHIEVEMENTS: ReadonlyArray<Achievement> = [
  {
    id: 'first_blood',
    name: 'First Blood',
    description: 'Land your first kill.',
    glyph: '◉',
    hue: 0,
    predicate: (c) => c.kills >= 1,
  },
  {
    id: 'spree',
    name: 'Killing Spree',
    description: '3 kills in a single life.',
    glyph: '◈',
    hue: 30,
    predicate: (c) => c.kills >= 3,
  },
  {
    id: 'carnage',
    name: 'Carnage',
    description: '5 kills in a single life.',
    glyph: '✦',
    hue: 65,
    predicate: (c) => c.kills >= 5,
  },
  {
    id: 'survivor',
    name: 'Survivor',
    description: 'Reach 1,000 mass.',
    glyph: '◐',
    hue: 110,
    predicate: (c) => c.mass >= 1000,
  },
  {
    id: 'behemoth',
    name: 'Behemoth',
    description: 'Reach 5,000 mass.',
    glyph: '◑',
    hue: 200,
    predicate: (c) => c.mass >= 5000,
  },
  {
    id: 'apex',
    name: 'Apex Predator',
    description: 'Reach 10,000 mass.',
    glyph: '◉',
    hue: 290,
    predicate: (c) => c.mass >= 10_000,
  },
  {
    id: 'combo_5',
    name: 'Combo Master',
    description: 'Chain a ×5 combo.',
    glyph: '✺',
    hue: 245,
    predicate: (c) => c.combo >= 5,
  },
  {
    id: 'veteran',
    name: 'Veteran',
    description: 'Finish 5 runs.',
    glyph: '✧',
    hue: 335,
    predicate: (c) => c.matchesPlayed >= 5,
  },
];

/** Returns ids that are newly satisfied by the context. */
export function evaluateAchievements(
  ctx: AchievementContext,
  alreadyUnlocked: ReadonlySet<string>,
): string[] {
  const fresh: string[] = [];
  for (const a of ACHIEVEMENTS) {
    if (alreadyUnlocked.has(a.id)) continue;
    if (a.predicate(ctx)) fresh.push(a.id);
  }
  return fresh;
}
